// Site photos + AI before/after renders, grouped into visual "blocks" — one
// per fence style. Each block has its own design + colour + notes, and can
// hold multiple site photos, each with up to 3 AI "after" images.
//
// Iter-71 rewrite. The old version passed `quote` from parent state and
// invalidated a query key the parent didn't own, so uploads/renders/deletes
// never refreshed the UI. This component now owns its own fetch state via
// useQuery on GET /quotes/{qid}/visuals (block-grouped payload).
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DesignPicker, DesignThumb } from "@/components/DesignPicker";
import { AuthImage } from "@/components/AuthImage";
import {
  Camera, Loader2, Sparkles, Trash2, Palette, Plus, Layers,
} from "lucide-react";

// Kept for backwards-compat consumers — the real catalog now lives in the backend.
export { AuthImage };
export const DESIGN_OPTIONS = [
  { value: "slat_fence", label: "Aluminium slat fence" },
  { value: "colorbond_panel_fence", label: "Colorbond panel fence" },
  { value: "picket_pool_flat_top", label: "Pool — flat-top picket" },
];

const MAX_RENDERS_PER_PHOTO = 3;

export const QuoteVisuals = ({ quoteId, onChanged }) => {
  const qc = useQueryClient();

  // Own our fetch — parent's `existing` state is set once at load and never
  // refreshes, which is what made the old UI stale after every mutation.
  const { data, isLoading } = useQuery({
    queryKey: ["quote-visuals", quoteId],
    queryFn: async () => (await api.get(`/quotes/${quoteId}/visuals`)).data,
    enabled: !!quoteId,
    staleTime: 0,
  });

  const blocks = data?.blocks || [];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["quote-visuals", quoteId] });
    onChanged?.();
  };

  const addBlock = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/visual-blocks`, {
      title: `Design ${blocks.length + 1}`,
    })).data,
    onSuccess: () => { toast.success("New design block added"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add block"),
  });

  if (!quoteId) return null;

  return (
    <Card className="rounded-sm industrial-shadow p-5" data-testid="quote-visuals-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="stamp-label text-cyan-600">Site photos &amp; visual preview</div>
          <p className="text-xs text-slate-400 mt-0.5">
            Each design block holds one fence style — pick a design + colour, add site photos and
            generate the AI &quot;after&quot; images to show the client. The customer only sees
            photos with a generated preview.
          </p>
        </div>
        <Button size="sm" onClick={() => addBlock.mutate()} disabled={addBlock.isPending}
          className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="add-visual-block-btn">
          {addBlock.isPending
            ? <Loader2 size={14} className="animate-spin mr-1.5" />
            : <Plus size={14} className="mr-1.5" />}
          Add design block
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading visuals…
        </div>
      )}

      {!isLoading && blocks.length === 0 && (
        <div className="mt-4 border-2 border-dashed border-slate-200 rounded-sm p-6 text-center text-sm text-slate-500"
          data-testid="visuals-empty">
          <Layers size={22} className="mx-auto mb-2 text-slate-300" />
          No design blocks yet. Click <b>Add design block</b> to start a before/after gallery.
        </div>
      )}

      <div className="space-y-4 mt-4">
        {blocks.map((block, idx) => (
          <VisualBlockCard
            key={block.id}
            quoteId={quoteId}
            block={block}
            index={idx}
            onChange={refresh}
          />
        ))}
      </div>
    </Card>
  );
};

function VisualBlockCard({ quoteId, block, index, onChange }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [picker, setPicker] = useState(false);
  const [title, setTitle] = useState(block.title || "");
  const [notes, setNotes] = useState(block.notes || "");
  const [heightMm, setHeightMm] = useState(block.height_mm || "");
  const [selection, setSelection] = useState({
    design: block.design, design_label: block.design_label,
    infill: block.infill, colour_name: block.colour_name, colour_hex: block.colour_hex,
  });
  const [renderingPhotoId, setRenderingPhotoId] = useState(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["quote-visuals", quoteId] });
    onChange?.();
  };

  const patchBlock = useMutation({
    mutationFn: async (patch) => (await api.patch(`/quotes/${quoteId}/visual-blocks/${block.id}`, patch)).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save block"),
  });

  const deleteBlock = useMutation({
    mutationFn: async () => api.delete(`/quotes/${quoteId}/visual-blocks/${block.id}`),
    onSuccess: () => { toast.success("Design block removed"); invalidate(); },
  });

  const uploadPhoto = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("block_id", block.id);
      return (await api.post(`/quotes/${quoteId}/site-photos`, fd)).data;
    },
    onSuccess: () => { toast.success("Site photo added"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const delPhoto = useMutation({
    mutationFn: async (pid) => api.delete(`/quotes/${quoteId}/site-photos/${pid}`),
    onSuccess: () => { toast.success("Photo removed"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const delRender = useMutation({
    mutationFn: async (rid) => api.delete(`/quotes/${quoteId}/renders/${rid}`),
    onSuccess: () => { toast.success("AI preview removed"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const doRender = useMutation({
    mutationFn: async (photoId) =>
      (await api.post(`/quotes/${quoteId}/renders`, {
        photo_id: photoId,
        design: selection.design || block.design,
        colour_name: selection.colour_name || block.colour_name,
        colour_hex: selection.colour_hex || block.colour_hex,
        height_mm: Number(heightMm) || null,
        custom_prompt: (notes || "").trim() || null,
      }, { timeout: 180000 })).data,
    onSuccess: () => {
      toast.success("AI preview generated");
      setRenderingPhotoId(null);
      invalidate();
    },
    onError: (e) => {
      setRenderingPhotoId(null);
      toast.error(e?.response?.data?.detail || "AI render failed — try again");
    },
  });

  const saveDesign = (next) => {
    setSelection(next);
    patchBlock.mutate({
      design: next.design, design_label: next.design_label,
      infill: next.infill, colour_name: next.colour_name, colour_hex: next.colour_hex,
    });
  };

  const commitTitle = () => {
    if ((title || "").trim() && title !== block.title) patchBlock.mutate({ title: title.trim() });
  };
  const commitNotes = () => {
    if (notes !== (block.notes || "")) patchBlock.mutate({ notes: notes });
  };
  const commitHeight = () => {
    const n = Number(heightMm) || null;
    if (n !== (block.height_mm || null)) patchBlock.mutate({ height_mm: n });
  };

  return (
    <div className="border border-slate-200 rounded-sm p-4 space-y-3 bg-slate-50/50"
      data-testid={`visual-block-${block.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center font-mono text-xs font-bold">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={commitTitle}
            placeholder="e.g. Front boundary — Colorbond 1.8m"
            className="rounded-sm h-9 bg-white font-semibold"
            data-testid={`block-${block.id}-title`} />
        </div>
        <Button size="sm" variant="ghost"
          onClick={() => window.confirm("Remove this whole design block and its photos?") && deleteBlock.mutate()}
          className="rounded-sm h-9 text-slate-400 hover:text-rose-600"
          disabled={deleteBlock.isPending}
          data-testid={`block-${block.id}-delete`}>
          <Trash2 size={14} className="mr-1" /> Delete
        </Button>
      </div>

      {/* Design + colour + notes + height row */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_120px] gap-3 items-end">
        <div>
          <Label className="stamp-label text-slate-600">Design</Label>
          <button type="button" onClick={() => setPicker(true)}
            className="mt-1 flex items-center gap-3 border-2 border-slate-200 hover:border-cyan-300 rounded-sm bg-white px-2.5 py-1.5 transition min-w-[220px]"
            data-testid={`block-${block.id}-open-picker`}>
            <div className="w-14 h-10 rounded-sm overflow-hidden border border-slate-100">
              <DesignThumb infill={selection.infill} colourHex={selection.colour_hex} size={60} />
            </div>
            <div className="text-left flex-1">
              <div className="text-xs font-semibold text-slate-800 truncate max-w-[160px]">{selection.design_label || "Choose design"}</div>
              <div className="text-[10px] text-slate-500 flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm border border-slate-300" style={{ background: selection.colour_hex }} />
                {selection.colour_name}
              </div>
            </div>
            <Palette size={14} className="text-cyan-600" />
          </button>
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Notes for AI (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={commitNotes}
            placeholder="e.g. include a single 1.0m gate on the left"
            className="rounded-sm bg-white text-sm mt-1 min-h-[38px] max-h-24"
            rows={2}
            data-testid={`block-${block.id}-notes`} />
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Height (mm)</Label>
          <Input type="number" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} onBlur={commitHeight}
            placeholder="1800" className="rounded-sm h-9 mt-1 bg-white"
            data-testid={`block-${block.id}-height`} />
        </div>
      </div>

      {/* Photos row */}
      <div className="pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="stamp-label text-slate-600">Site photos ({(block.photos || []).length})</div>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
            disabled={uploadPhoto.isPending} className="rounded-sm h-8 bg-white"
            data-testid={`block-${block.id}-add-photo`}>
            {uploadPhoto.isPending
              ? <Loader2 size={13} className="animate-spin mr-1.5" />
              : <Camera size={13} className="mr-1.5" />} Add site photo
          </Button>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadPhoto.mutate(e.target.files[0])}
            data-testid={`block-${block.id}-photo-input`} />
        </div>

        {(block.photos || []).length === 0 && (
          <p className="text-xs text-slate-400 text-center py-3">No photos yet — add one to generate a before/after for the client.</p>
        )}

        <div className="space-y-3">
          {(block.photos || []).map((photo) => {
            const atLimit = (photo.renders || []).length >= MAX_RENDERS_PER_PHOTO;
            const rendering = doRender.isPending && renderingPhotoId === photo.id;
            return (
              <div key={photo.id} className="border border-slate-200 rounded-sm p-3 bg-white"
                data-testid={`photo-${photo.id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Before</div>
                    <AuthImage quoteId={quoteId} fileId={photo.id}
                      className="w-full h-40 object-cover rounded-sm border border-slate-200" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-cyan-600 mb-1">
                      AI previews ({(photo.renders || []).length}/{MAX_RENDERS_PER_PHOTO})
                    </div>
                    {(photo.renders || []).length === 0 ? (
                      <div className="h-40 rounded-sm border border-dashed border-cyan-200 bg-cyan-50/40 flex flex-col items-center justify-center text-xs text-cyan-700">
                        <Sparkles size={16} className="mb-1" />
                        No previews yet — click <b>Generate</b> below
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {(photo.renders || []).map((r) => (
                          <div key={r.id} className="relative group">
                            <AuthImage quoteId={quoteId} fileId={r.id}
                              className="w-full h-40 object-cover rounded-sm border border-cyan-200" />
                            <div className="text-[9px] text-slate-500 mt-0.5 truncate">
                              {(r.design || "").replace(/_/g, " ")} · {r.colour_name}
                            </div>
                            <button type="button"
                              onClick={() => window.confirm("Remove this AI preview?") && delRender.mutate(r.id)}
                              className="absolute top-1 right-1 w-6 h-6 bg-rose-500 hover:bg-rose-600 text-white rounded-sm flex items-center justify-center opacity-100 shadow"
                              data-testid={`render-${r.id}-delete`}
                              title="Remove this preview">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm"
                    onClick={() => { setRenderingPhotoId(photo.id); doRender.mutate(photo.id); }}
                    disabled={rendering || atLimit || doRender.isPending}
                    className="rounded-sm h-8 bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid={`photo-${photo.id}-generate`}>
                    {rendering
                      ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Rendering… ~20s</>
                      : <><Sparkles size={13} className="mr-1.5" /> {atLimit ? "Max 3 reached" : "Generate AI preview"}</>}
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => window.confirm("Remove this photo and its AI previews?") && delPhoto.mutate(photo.id)}
                    disabled={delPhoto.isPending}
                    className="rounded-sm h-8 text-slate-400 hover:text-rose-600 ml-auto"
                    data-testid={`photo-${photo.id}-delete`}>
                    <Trash2 size={13} className="mr-1" /> Remove photo
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <DesignPicker open={picker} onOpenChange={setPicker} value={selection} onChange={saveDesign} />
    </div>
  );
}

export default QuoteVisuals;
